import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 天文宇宙 - 竖屏「星际探索」风格
 *
 * 核心亮点：
 * - 星空粒子背景（闪烁 + 缓动）
 * - 行星轨道 SVG 动画（椭圆轨道 + 行星运动）
 * - 尺度对比可视化（地球 vs 太阳 vs 恒星）
 * - 数据标注弹出
 * - 深空渐变背景 + 星云光效
 *
 * 尺寸：1080x1920 竖屏
 */

interface CelestialBody {
  name: string;
  radius: number;    // relative, for display
  orbitRadius: number;
  color: string;
  period: number;     // frames per revolution
  info: string;
}

interface ScaleComparison {
  name: string;
  size: number;       // relative
  color: string;
  realSize: string;
}

interface AstronomyTemplateProps {
  topic: string;
  subtitle: string;
  bodies: CelestialBody[];
  scaleComparisons: ScaleComparison[];
  fact: string;
  keyNumber: { value: string; label: string };
  explanation: string[];
}

const DEFAULT_PROPS: AstronomyTemplateProps = {
  topic: '太阳系的真实尺度',
  subtitle: 'The True Scale of Our Solar System',
  bodies: [
    { name: '水星', radius: 16, orbitRadius: 140, color: '#b0b0b0', period: 120, info: '离太阳最近' },
    { name: '金星', radius: 24, orbitRadius: 200, color: '#e8a838', period: 180, info: '最热的行星' },
    { name: '地球', radius: 28, orbitRadius: 270, color: '#4a90d9', period: 240, info: '我们的家园' },
    { name: '火星', radius: 20, orbitRadius: 340, color: '#d44a2a', period: 340, info: '下一个目标' },
    { name: '木星', radius: 56, orbitRadius: 440, color: '#c8956a', period: 600, info: '最大的行星' },
  ],
  scaleComparisons: [
    { name: '地球', size: 1, color: '#4a90d9', realSize: '12,742 km' },
    { name: '木星', size: 11, color: '#c8956a', realSize: '139,820 km' },
    { name: '太阳', size: 109, color: '#fbbf24', realSize: '1,392,000 km' },
  ],
  fact: '如果太阳是一个篮球，地球只有一粒芝麻大小，它们之间距离约 26 米',
  keyNumber: { value: '1,392,000', label: '太阳直径（km）' },
  explanation: [
    '太阳占据了太阳系 99.86% 的质量',
    '光从太阳到地球需要 8 分 20 秒',
    '木星的体积可以装下 1,300 个地球',
  ],
};

const Star: React.FC<{ x: number; y: number; size: number; delay: number }> = ({ x, y, size, delay }) => {
  const frame = useCurrentFrame();
  const twinkle = interpolate(Math.sin((frame + delay) / (8 + delay % 5)), [-1, 1], [0.2, 1]);
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'white',
        opacity: twinkle,
        boxShadow: `0 0 ${size * 2}px rgba(255,255,255,${twinkle * 0.5})`,
      }}
    />
  );
};

export const AstronomyTemplate: React.FC<Partial<AstronomyTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Layout
  const solarCx = 540;
  const solarCy = 720;

  // Phases
  const titleProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const orbitDraw = interpolate(frame, [40, 100], [0, 1], { extrapolateRight: 'clamp' });

  // Key number
  const numStart = 60;
  const numOpacity = interpolate(frame, [numStart, numStart + 20], [0, 1], { extrapolateRight: 'clamp' });

  // Scale comparison
  const scaleStart = 250;

  // Fact
  const factStart = scaleStart + p.scaleComparisons.length * 40 + 30;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Explanation
  const expStart = factStart + 40;

  // Stars
  const stars = Array.from({ length: 60 }).map((_, i) => ({
    x: ((i * 137.5 + 23) % 1080),
    y: ((i * 89.3 + 47) % 1920),
    size: 1 + (i % 3),
    delay: i * 7,
  }));

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 50% 30%, #0a0a2e 0%, #050510 50%, #000005 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Stars */}
      {stars.map((s, i) => (
        <Star key={i} {...s} />
      ))}

      {/* Nebula glow */}
      <div
        style={{
          position: 'absolute',
          left: 200,
          top: 300,
          width: 600,
          height: 600,
          background: 'radial-gradient(circle, rgba(99,102,241,0.04), transparent 70%)',
          borderRadius: '50%',
        }}
      />

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * -15}px)`,
        }}
      >
        <div style={{ display: 'inline-block', padding: '5px 16px', background: '#818cf818', border: '1px solid #818cf830', borderRadius: 8, fontSize: 30, color: '#818cf8', fontWeight: 600, letterSpacing: '0.15em', marginBottom: 12 }}>
          🌌 ASTRONOMY
        </div>
        <h1 style={{ fontSize: 60, fontWeight: 800, margin: 0 }}>{p.topic}</h1>
        <p style={{ fontSize: 40, color: 'rgba(255,255,255,0.3)', marginTop: 6, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* Key number */}
      <div
        style={{
          position: 'absolute',
          top: 360,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: numOpacity,
        }}
      >
        <div style={{ fontSize: 76, fontWeight: 900, color: '#fbbf24', fontVariantNumeric: 'tabular-nums' }}>
          {p.keyNumber.value}
        </div>
        <div style={{ fontSize: 40, color: 'rgba(255,255,255,0.4)', marginTop: 4, marginLeft: 30 }}>{p.keyNumber.label}</div>
      </div>

      {/* ========== SOLAR SYSTEM ORBITS ========== */}
      <svg
        style={{ position: 'absolute', left: 0, top: 0 }}
        width="1080"
        height="1920"
        viewBox="0 0 1080 1920"
      >
        {/* Sun */}
        <circle cx={solarCx} cy={solarCy} r={60 * orbitDraw} fill="#fbbf24" opacity="0.9" />
        <circle cx={solarCx} cy={solarCy} r={90 * orbitDraw} fill="#fbbf24" opacity="0.08" />
        <circle cx={solarCx} cy={solarCy} r={120 * orbitDraw} fill="#fbbf24" opacity="0.03" />

        {/* Orbits + Planets */}
        {p.bodies.map((body, i) => {
          const orbitOpacity = interpolate(frame, [40 + i * 10, 60 + i * 10], [0, 0.15], { extrapolateRight: 'clamp' });
          const angle = (frame / body.period) * Math.PI * 2;
          const px = solarCx + body.orbitRadius * Math.cos(angle);
          const py = solarCy + body.orbitRadius * 0.5 * Math.sin(angle);

          const planetVisible = interpolate(frame, [60 + i * 15, 75 + i * 15], [0, 1], { extrapolateRight: 'clamp' });

          return (
            <g key={i}>
              {/* Orbit ellipse */}
              <ellipse
                cx={solarCx}
                cy={solarCy}
                rx={body.orbitRadius}
                ry={body.orbitRadius * 0.5}
                fill="none"
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
                strokeDasharray="4 4"
                opacity={orbitOpacity / 0.15}
              />

              {/* Planet */}
              <g opacity={planetVisible}>
                <circle cx={px} cy={py} r={body.radius * 1.8} fill={body.color} opacity="0.08" />
                <circle cx={px} cy={py} r={body.radius} fill={body.color} />

                {/* Name label */}
                <text
                  x={px}
                  y={py + body.radius + 28}
                  fill="rgba(255,255,255,0.6)"
                  fontSize="22"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  {body.name}
                </text>
              </g>
            </g>
          );
        })}
      </svg>

      {/* ========== SCALE COMPARISON ========== */}
      <div
        style={{
          position: 'absolute',
          top: 930,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 50, color: 'rgba(255,255,255,0.3)', marginBottom: 100, letterSpacing: '0.1em' }}>
          尺度对比
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 40, height: 180 }}>
          {p.scaleComparisons.map((sc, i) => {
            const start = scaleStart + i * 40;
            const scaleAnim = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 10, stiffness: 100, mass: 0.5 },
            });
            const displaySize = Math.min(sc.size * 12, 160) * scaleAnim;

            return (
              <div key={i} style={{ textAlign: 'center', opacity: scaleAnim }}>
                <div
                  style={{
                    width: displaySize,
                    height: displaySize,
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 35%, ${sc.color}, ${sc.color}80)`,
                    margin: '0 auto',
                    boxShadow: `0 0 ${displaySize / 2}px ${sc.color}30`,
                  }}
                />
                <div style={{ fontSize: 40, fontWeight: 700, color: sc.color, marginTop: 12 }}>{sc.name}</div>
                <div style={{ fontSize: 40, color: 'rgba(255,255,255,0.3)' }}>{sc.realSize}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fact */}
      <div
        style={{
          position: 'absolute',
          top: 1360,
          left: 60,
          right: 60,
          opacity: factOpacity,
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            background: 'rgba(251,191,36,0.05)',
            border: '1px solid rgba(251,191,36,0.15)',
            borderRadius: 18,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 38, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)' }}>🌍 {p.fact}</div>
        </div>
      </div>

      {/* Explanation */}
      <div
        style={{
          position: 'absolute',
          top: 1600,
          left: 60,
          right: 60,
        }}
      >
        {p.explanation.map((line, i) => {
          const start = expStart + i * 30;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          return (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, opacity, alignItems: 'center' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#818cf8', opacity: 0.5, flexShrink: 0 }} />
              <span style={{ fontSize: 40, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{line}</span>
            </div>
          );
        })}
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #4a90d9, #fbbf24, #ef4444)',
        }}
      />
    </AbsoluteFill>
  );
};
